import { useState, useRef, useEffect } from 'react'
import { useCrosswordStore } from '../store'

type TagEditorProps = {
  tags: string[]
  onChange: (tags: string[]) => void
  // Compact = no surrounding header / chrome — just the chips + add affordance.
  compact?: boolean
}

/**
 * Inline tag editor — chips with × removal + a small "+" affordance that opens
 * an autocomplete input pulling from the store's tag registry. Used by the
 * pair detail card and the word assistant card.
 */
export function TagEditor({ tags, onChange, compact = false }: TagEditorProps) {
  const registry = useCrosswordStore(s => s.tags)
  const registerTag = useCrosswordStore(s => s.registerTag)
  const [adding, setAdding] = useState(false)
  const [text, setText] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (adding) inputRef.current?.focus()
  }, [adding])

  const remove = (tag: string) => onChange(tags.filter(t => t !== tag))

  const commit = () => {
    const cleaned = text.trim()
    if (cleaned && !tags.includes(cleaned)) {
      onChange([...tags, cleaned])
      registerTag(cleaned)
    }
    setText('')
    setAdding(false)
  }

  // Suggestion list: existing registry entries that aren't already on this
  // entity AND prefix-match the typed text. Empty input → top 8 unassigned.
  const suggestions = (() => {
    const owned = new Set(tags)
    const lower = text.toLowerCase()
    const filtered = registry.filter(t => !owned.has(t) && t.toLowerCase().includes(lower))
    return filtered.slice(0, 8)
  })()

  return (
    <div className={compact ? '' : 'space-y-2'}>
      <div className="flex flex-wrap gap-1 items-center">
        {tags.map(tag => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded bg-gray-100 text-gray-700 border border-gray-200"
          >
            <span>{tag}</span>
            <button
              onClick={() => remove(tag)}
              className="text-gray-400 hover:text-gray-700 transition-colors"
              title={`Remove ${tag}`}
            >
              ×
            </button>
          </span>
        ))}
        {adding ? (
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={text}
              onChange={e => setText(e.target.value)}
              onBlur={() => {
                // Delay commit so suggestion clicks land first.
                setTimeout(() => {
                  if (adding) commit()
                }, 100)
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  commit()
                } else if (e.key === 'Escape') {
                  setText('')
                  setAdding(false)
                }
              }}
              placeholder="tag…"
              className="px-2 py-0.5 text-[11px] border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 w-24"
            />
            {suggestions.length > 0 && (
              <div className="absolute top-full left-0 mt-0.5 z-10 bg-white border border-gray-200 rounded shadow-sm max-h-40 overflow-y-auto min-w-[8rem]">
                {suggestions.map(s => (
                  <button
                    key={s}
                    onMouseDown={e => {
                      e.preventDefault()
                      onChange([...tags, s])
                      setText('')
                      setAdding(false)
                    }}
                    className="block w-full text-left px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-100"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => setAdding(true)}
            className="px-2 py-0.5 text-[11px] text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded border border-dashed border-gray-300 transition-colors"
            title="Add tag"
          >
            + tag
          </button>
        )}
      </div>
    </div>
  )
}
